'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { MfaStatusBadge } from '@/components/Badge/MfaStatusBadge';
import { ProductLogoMediumSoftware } from '@/components/Logo';
import { Text } from '@/components/Typography';
import { useSoftwareCategoryLabel } from '@/features/software/category/useSoftwareCategoryLabel';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { isForbiddenError } from '@/lib/api/is-forbidden-error';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { ledgerColumn } from '../-shared/-components/ledgerColumn';
import { LedgerContent } from '../-shared/-components/LedgerContent';
import { LedgerDataTable } from '../-shared/-components/LedgerDataTable';
import { LedgerForbiddenState } from '../-shared/-components/LedgerForbiddenState';
import { LedgerHeader } from '../-shared/-components/LedgerHeader';
import { Field, FieldGrid, ResourceDetailCard } from '../-shared/-components/ResourceDetailCard';
import { useAccountLedger } from '../-shared/-query';
import { formatDate } from '../-shared/-utils';
const ACCOUNTS_PATH = '/individual/portal/lookup/accounts';
const LOOKUP_PATH = '/individual/portal/lookup';
const accountsSearchSchema = z.object({
  id: z.string().optional(),
  pageIndex: z.coerce.number().int().min(0).catch(0),
  pageSize: z.coerce.number().int().min(1).max(100).catch(20)
});
export const Route = createFileRoute('/_individual/individual/portal/lookup/_ledger/accounts/')({
  validateSearch: accountsSearchSchema,
  component: AccountsLookupPage
});
type AccountLedgerItem = NonNullable<ReturnType<typeof useAccountLedger>['data']>['items'][number];
function AccountsLookupPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.lookupResources'
  });
  const {
    t: ct
  } = useTranslation(defaultNS, {
    keyPrefix: 'common.breadcrumbs'
  });
  const {
    t: th
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.home'
  });
  const {
    id,
    pageIndex,
    pageSize
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  const categoryLabel = useSoftwareCategoryLabel();
  const [freeText, setFreeText] = useState('');
  const debouncedSetFreeText = useDebouncedCallback((text: string) => {
    setFreeText(text);
    // 検索語変更時は 1 ページ目に戻す（存在しないページで空表示になるのを防ぐ）。
    void navigate({
      search: prev => ({
        ...prev,
        pageIndex: 0
      })
    });
  }, []);
  const {
    data,
    isPending,
    isError,
    error
  } = useAccountLedger({
    page: pageIndex + 1,
    pageSize,
    freeText: freeText === '' ? undefined : freeText
  });
  const selected = id != null ? data?.items.find(a => a.key === id) ?? null : null;
  const mfaLabel = (enabled: boolean) => enabled ? t('accounts.mfa.enabled') : t('accounts.mfa.disabled');
  if (isError && isForbiddenError(error)) {
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('accounts.title')
      }]} Logo={ProductLogoMediumSoftware} title={t('accounts.title')} />
        <LedgerContent>
          <LedgerForbiddenState onBack={() => void navigate({
          to: LOOKUP_PATH
        })} />
        </LedgerContent>
      </>;
  }
  if (isError) {
    throw error;
  }
  if (selected != null) {
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('accounts.title'),
        link: {
          to: ACCOUNTS_PATH,
          search: {
            pageIndex,
            pageSize
          }
        }
      }, {
        label: selected.software.name
      }]} Logo={ProductLogoMediumSoftware} title={selected.software.name} subtitle={selected.user?.email ?? undefined} />
        <LedgerContent>
          <ResourceDetailCard title={t('accounts.detail.basicInfo')}>
            <FieldGrid>
              <Field label={t('accounts.detail.app')} value={selected.software.name} />
              <Field label={t('accounts.detail.category')} value={categoryLabel(selected.software.category) ?? '—'} />
              <Field label={t('accounts.detail.tenantName')} value={selected.appIntegration?.tenantName ?? '—'} />
              <Field label={t('accounts.detail.displayName')} value={selected.user?.displayName ?? selected.user?.name ?? '—'} />
              <Field label={t('accounts.detail.actualRoleName')} value={selected.user?.actualRoleName ?? '—'} />
              <Field label={t('accounts.detail.mfa')} value={<MfaStatusBadge enabled={selected.user?.configuredMfa ?? false} label={mfaLabel(selected.user?.configuredMfa ?? false)} />} />
              <Field label={t('accounts.detail.createdAt')} value={formatDate(selected.user?.createdAt ?? null)} />
              <Field label={t('accounts.detail.lastSignIn')} value={formatDate(selected.user?.lastSignedInAt ?? null)} />
            </FieldGrid>
          </ResourceDetailCard>
        </LedgerContent>
      </>;
  }
  const columns = [ledgerColumn<AccountLedgerItem>({
    id: 'app',
    header: t('accounts.headers.app'),
    cell: a => <Text variant="label">{a.software.name}</Text>
  }), ledgerColumn<AccountLedgerItem>({
    id: 'category',
    header: t('accounts.headers.category'),
    cell: a => categoryLabel(a.software.category) ?? '—'
  }), ledgerColumn<AccountLedgerItem>({
    id: 'tenantName',
    header: t('accounts.headers.tenantName'),
    cell: a => a.appIntegration?.tenantName ?? '—'
  }), ledgerColumn<AccountLedgerItem>({
    id: 'displayName',
    header: t('accounts.headers.displayName'),
    cell: a => a.user?.displayName ?? a.user?.name ?? '—'
  }), ledgerColumn<AccountLedgerItem>({
    id: 'actualRoleName',
    header: t('accounts.headers.actualRoleName'),
    cell: a => a.user?.actualRoleName ?? '—'
  }), ledgerColumn<AccountLedgerItem>({
    id: 'createdAt',
    header: t('accounts.headers.createdAt'),
    cell: a => formatDate(a.user?.createdAt ?? null)
  }), ledgerColumn<AccountLedgerItem>({
    id: 'lastSignedInAt',
    header: t('accounts.headers.lastSignIn'),
    cell: a => formatDate(a.user?.lastSignedInAt ?? null)
  }), ledgerColumn<AccountLedgerItem>({
    id: 'mfa',
    header: t('accounts.headers.mfa'),
    cell: a => <MfaStatusBadge enabled={a.user?.configuredMfa ?? false} label={mfaLabel(a.user?.configuredMfa ?? false)} />
  })];
  return <>
      <LedgerHeader breadcrumbs={[{
      label: ct('home'),
      link: {
        to: '/'
      }
    }, {
      label: th('lookup.title'),
      link: {
        to: LOOKUP_PATH
      }
    }, {
      label: t('accounts.title')
    }]} Logo={ProductLogoMediumSoftware} title={t('accounts.title')} />
      <LedgerContent className="h-full">
        <LedgerDataTable<AccountLedgerItem> fillHeight manualPagination columns={columns} data={data?.items} isPending={isPending} pageCount={data?.maxPage ?? 0} totalCount={data?.count} pagination={{
        pageIndex,
        pageSize
      }} onPaginationChange={updater => {
        const next = typeof updater === 'function' ? updater({
          pageIndex,
          pageSize
        }) : updater;
        void navigate({
          search: prev => ({
            ...prev,
            pageIndex: next.pageIndex,
            pageSize: next.pageSize
          })
        });
      }} onSearchTextChange={debouncedSetFreeText} getRowId={a => a.key} searchPlaceholder={t('common.search')} onRowSelect={a => void navigate({
        search: prev => ({
          ...prev,
          id: a.key
        })
      })} emptyContent={<Text variant="p" className="text-neutral-muted-on-fill">
              {t('common.empty')}
            </Text>} />
      </LedgerContent>
    </>;
}